import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useReviewStore, Review } from '../store/reviewStore';
import { useAuthStore } from '../store/authStore';

interface ReviewSectionProps {
  productId: number;
  productName: string;
}

const ReviewSection: React.FC<ReviewSectionProps> = ({ productId, productName }) => {
  const { t } = useTranslation();
  const { addReview, getReviewsByProduct, getProductRating, hasUserReviewed } = useReviewStore();
  const { user, isAuthenticated } = useAuthStore();
  
  const [showForm, setShowForm] = useState(false);
  const [rating, setRating] = useState(5);
  const [hoverRating, setHoverRating] = useState(0);
  const [title, setTitle] = useState('');
  const [comment, setComment] = useState('');
  const [submitted, setSubmitted] = useState(false);

  const reviews = getReviewsByProduct(productId);
  const { average, count } = getProductRating(productId);
  const userReviewed = user ? hasUserReviewed(productId, user.id) : false;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!user || !title.trim() || !comment.trim()) return;

    addReview({
      productId,
      userId: user.id,
      userName: `${user.firstName} ${user.lastName}`,
      rating,
      title: title.trim(),
      comment: comment.trim(),
      verified: true,
    });

    setSubmitted(true);
    setShowForm(false);
    setTitle('');
    setComment('');
    setRating(5);
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('tr-TR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const ratingDistribution = [5, 4, 3, 2, 1].map((star) => ({
    star,
    count: reviews.filter((r) => r.rating === star).length,
    percentage: reviews.length > 0
      ? (reviews.filter((r) => r.rating === star).length / reviews.length) * 100
      : 0,
  }));

  return (
    <div className="bg-white rounded-2xl border border-gray-100 p-6 mt-6">
      <h3 className="text-lg font-bold text-gray-800 mb-6 flex items-center gap-2">
        <span>⭐</span>
        {t('reviews.title', { defaultValue: 'Ürün Değerlendirmeleri' })}
      </h3>

      {/* Rating Summary */}
      {count > 0 && (
        <div className="flex flex-col sm:flex-row gap-6 mb-6 pb-6 border-b border-gray-100">
          <div className="flex flex-col items-center justify-center min-w-[120px]">
            <span className="text-4xl font-black text-gray-800">{average}</span>
            <div className="flex gap-0.5 my-1">
              {[1, 2, 3, 4, 5].map((star) => (
                <svg
                  key={star}
                  className={`w-4 h-4 ${star <= Math.round(average) ? 'text-amber-400' : 'text-gray-200'}`}
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                </svg>
              ))}
            </div>
            <span className="text-xs text-gray-500">{count} {t('reviews.reviews', { defaultValue: 'değerlendirme' })}</span>
          </div>

          <div className="flex-1 space-y-1.5">
            {ratingDistribution.map((dist) => (
              <div key={dist.star} className="flex items-center gap-2">
                <span className="text-xs text-gray-500 w-3">{dist.star}</span>
                <svg className="w-3 h-3 text-amber-400" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                </svg>
                <div className="flex-1 h-2 bg-gray-100 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-amber-400 rounded-full transition-all duration-500"
                    style={{ width: `${dist.percentage}%` }}
                  />
                </div>
                <span className="text-xs text-gray-400 w-6 text-right">{dist.count}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Success Message */}
      {submitted && (
        <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-xl text-green-700 text-sm">
          ✅ {t('reviews.successMessage', { defaultValue: 'Değerlendirmeniz başarıyla eklendi!' })}
        </div>
      )}

      {/* Add Review Button */}
      {isAuthenticated && !userReviewed && !submitted && (
        <div className="mb-6">
          {!showForm ? (
            <button
              onClick={() => setShowForm(true)}
              className="px-6 py-3 bg-gradient-to-r from-[#E31E24] to-[#FF4500] text-white font-bold rounded-xl hover:shadow-lg transition-all duration-300 text-sm"
            >
              ✍️ {t('reviews.writeReview', { defaultValue: 'Değerlendirme Yaz' })}
            </button>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4 p-4 bg-gray-50 rounded-xl">
              {/* Star Rating */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  {t('reviews.yourRating', { defaultValue: 'Puanınız' })}
                </label>
                <div className="flex gap-1">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <button
                      key={star}
                      type="button"
                      onMouseEnter={() => setHoverRating(star)}
                      onMouseLeave={() => setHoverRating(0)}
                      onClick={() => setRating(star)}
                      className="transition-transform hover:scale-110"
                    >
                      <svg
                        className={`w-7 h-7 ${
                          star <= (hoverRating || rating) ? 'text-amber-400' : 'text-gray-200'
                        }`}
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                      </svg>
                    </button>
                  ))}
                </div>
              </div>

              {/* Title */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">
                  {t('reviews.reviewTitle', { defaultValue: 'Başlık' })}
                </label>
                <input
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder={t('reviews.titlePlaceholder', { defaultValue: 'Ürün hakkında kısa bir özet...' })}
                  required
                  className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm outline-none focus:border-[#E31E24] transition-colors"
                />
              </div>

              {/* Comment */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">
                  {t('reviews.yourReview', { defaultValue: 'Yorumunuz' })}
                </label>
                <textarea
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  placeholder={t('reviews.commentPlaceholder', { defaultValue: 'Ürünü kullandıktan sonra deneyiminizi paylaşın...' })}
                  required
                  rows={4}
                  className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm outline-none focus:border-[#E31E24] transition-colors resize-none"
                />
              </div>

              <div className="flex gap-3">
                <button
                  type="submit"
                  className="px-6 py-2.5 bg-gradient-to-r from-[#E31E24] to-[#FF4500] text-white font-bold rounded-xl text-sm hover:shadow-lg transition-all"
                >
                  {t('reviews.submit', { defaultValue: 'Gönder' })}
                </button>
                <button
                  type="button"
                  onClick={() => setShowForm(false)}
                  className="px-6 py-2.5 bg-gray-100 text-gray-600 font-semibold rounded-xl text-sm hover:bg-gray-200 transition-all"
                >
                  {t('reviews.cancel', { defaultValue: 'İptal' })}
                </button>
              </div>
            </form>
          )}
        </div>
      )}

      {/* Login Prompt */}
      {!isAuthenticated && (
        <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-xl text-blue-700 text-sm">
          🔐 {t('reviews.loginRequired', { defaultValue: 'Değerlendirme yapmak için giriş yapmalısınız.' })}
        </div>
      )}

      {/* Reviews List */}
      <div className="space-y-4">
        {reviews.length === 0 ? (
          <p className="text-gray-400 text-sm text-center py-8">
            {t('reviews.noReviews', { defaultValue: 'Henüz değerlendirme yapılmamış. İlk değerlendiren siz olun!' })}
          </p>
        ) : (
          reviews.map((review) => (
            <div key={review.id} className="p-4 bg-gray-50 rounded-xl">
              <div className="flex items-start justify-between mb-2">
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <div className="flex gap-0.5">
                      {[1, 2, 3, 4, 5].map((star) => (
                        <svg
                          key={star}
                          className={`w-3.5 h-3.5 ${star <= review.rating ? 'text-amber-400' : 'text-gray-200'}`}
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                        </svg>
                      ))}
                    </div>
                    {review.verified && (
                      <span className="text-[10px] bg-green-100 text-green-700 px-1.5 py-0.5 rounded-full font-medium">
                        ✓ {t('reviews.verified', { defaultValue: 'Doğrulanmış Alışveriş' })}
                      </span>
                    )}
                  </div>
                  <h4 className="text-sm font-semibold text-gray-800">{review.title}</h4>
                </div>
                <span className="text-xs text-gray-400">{formatDate(review.createdAt)}</span>
              </div>
              <p className="text-sm text-gray-600 mb-2">{review.comment}</p>
              <span className="text-xs text-gray-400">— {review.userName}</span>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default ReviewSection;
